import React from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
    lobbyPersonBox: {
        width: '100%',
        aspectRatio: '3 / 4.35',
        borderRadius: 4,
        background: 'rgba(28, 30, 32, 0.6)',
        border: '1px dashed rgba(255,255,255,0.15)',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.15s ease-in-out, border-color 0.15s ease-in-out',
        boxSizing: 'border-box',
        '&:hover': {
          borderColor: '#E8A33D',
          background: 'rgba(232,163,61,0.05)',
          '& .addIcon': {
            color: '#ffffff',
          },
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    pendingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    pendingProgress: {
        [theme.breakpoints.up("xl")]: {
            width: '2.5rem !important',
            height: '2.5rem !important',
        },
    },
    pendingName: {
        color: '#ffffff',
        fontSize: '1rem',
        fontWeight: 700,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        display: 'block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.3rem',
        },
    },
    pendingLabel: {
        color: '#e0e0e0',
        fontSize: '1rem',
        marginTop: '0.2rem',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.3rem',
            marginTop: '0.4rem',
        },
    },
    pendingFriendData: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '0 1rem',
        boxSizing: 'border-box',
        marginTop: '0.4rem',
        gap: '0.4rem',
    },
    playerAvatar: {
        width: '2rem',
        height: '2rem',
        margin: 0,
        [theme.breakpoints.up("xl")]: {
            width: '2.8rem',
            height: '2.8rem',
        },
    },
    addIcon: {
        fontSize: '1.35rem',
        color: '#ffffff',
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
        transition: 'color 0.15s ease-in-out',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.45rem',
        },
    },
    inviteText: {
        color: '#ffffff',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.62rem',
        fontWeight: 500,
        marginTop: 10,
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.66rem',
        },
    },
}));

export default function LobbyInvitePlayersBox({ pendingInvite = null }) {
    const classes = useStyles();

    const avatarSrc = resolveAvatarSrc({
      basePath,
      picture: pendingInvite?.picture,
      userId: pendingInvite?.userId || pendingInvite?._id,
      avatarUpdatedAtMs: pickAvatarUpdatedAtMs(pendingInvite),
      hasCustomAvatar: !!pendingInvite?.avatar,
    });

    return (
        <div className={classes.lobbyPersonBox}>
            {pendingInvite ? (
                <div className={classes.pendingContainer}>
                    <CircularProgress size={35} className={classes.pendingProgress} />
                    <Typography className={classes.pendingLabel}>Inviting</Typography>
                    <div className={classes.pendingFriendData}> 
                        <Avatar className={classes.playerAvatar} size="large" src={avatarSrc} />
                        <Typography className={classes.pendingName}>{pendingInvite.nickname}</Typography>
                    </div>
                </div>
            ) : (
                <>
                    <PersonAddAltOutlinedIcon className={`${classes.addIcon} addIcon`} />
                    <Typography className={`${classes.inviteText} inviteText`}>
                        EMPTY SLOT
                    </Typography>
                </>
            )}
        </div>
    );
}
